import { useConfigStore } from '@/store/config-store'
import { useAuthStore } from '@/store/auth-store'
import { APP } from '@/lib/constants'
import type { PrintData } from '@/hardware/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Product {
  name: string
  price: number
  is_available_borne: boolean
  has_sequence: boolean
  category?: string
  image_url?: string
}

export interface TransactionData {
  transaction_id: string
  products: Array<{ key: string; quantity: number }>
  payments: Array<{ type: string; amount: number }>
  phone_number: string | null
  total: number
}

// ---------------------------------------------------------------------------
// CSRF helper
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/)
  return match ? match[1] : ''
}

// ---------------------------------------------------------------------------
// Agent Client
// ---------------------------------------------------------------------------

class AgentClient {
  // ---- connection stats ---------------------------------------------------

  private stats = {
    totalRequests: 0,
    failedRequests: 0,
    responseTimes: [] as number[], // last 100 response times in ms
  }

  // ---- private helpers ----------------------------------------------------

  private getHeaders(method?: string): Record<string, string> {
    const { borneId, borneName } = useConfigStore.getState()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Borne-ID': borneId,
      'X-Borne-Name': borneName,
    }

    // Add CSRF token for mutating requests
    if (method && method !== 'GET' && method !== 'HEAD') {
      headers['X-CSRF-TOKEN'] = getCsrfToken()
    }

    return headers
  }

  // ---- generic request with retry ----------------------------------------

  async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `/api${endpoint}`
    const method = options?.method ?? 'GET'
    const headers = { ...this.getHeaders(method), ...(options?.headers as Record<string, string>) }

    this.stats.totalRequests++
    const requestStart = Date.now()

    const delays = APP.RETRY_DELAYS.slice(0, APP.MAX_RETRIES)
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= APP.MAX_RETRIES; attempt++) {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), APP.API_TIMEOUT)

      try {
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
          signal: controller.signal,
        })

        clearTimeout(timer)

        // On 401, logout and stop retrying
        if (response.status === 401) {
          useAuthStore.getState().logout()
          throw new Error('Session expiree')
        }

        if (!response.ok) {
          const body = await response.text().catch(() => '')
          throw new Error(`HTTP ${response.status}: ${body}`)
        }

        const result = (await response.json()) as T

        // Track successful response time
        const elapsed = Date.now() - requestStart
        this.stats.responseTimes.push(elapsed)
        if (this.stats.responseTimes.length > 100) {
          this.stats.responseTimes.shift()
        }

        return result
      } catch (err) {
        clearTimeout(timer)
        lastError = err instanceof Error ? err : new Error(String(err))

        // Don't retry on auth errors
        if (lastError.message === 'Session expiree') break

        if (attempt < APP.MAX_RETRIES) {
          await this.delay(delays[attempt])
        }
      }
    }

    // Track failed request
    this.stats.failedRequests++
    const elapsed = Date.now() - requestStart
    this.stats.responseTimes.push(elapsed)
    if (this.stats.responseTimes.length > 100) {
      this.stats.responseTimes.shift()
    }

    throw lastError ?? new Error('Request failed after retries')
  }

  // ---- public API ---------------------------------------------------------

  async testConnection(): Promise<boolean> {
    try {
      await this.request<{ status: string }>('/health')
      return true
    } catch {
      return false
    }
  }

  async getProducts(): Promise<Record<string, Product>> {
    const data = await this.request<{ products: Record<string, Product> }>('/pos/products/borne')
    return data.products
  }

  async submitTransaction(
    data: TransactionData,
  ): Promise<{ success: boolean; transaction_id: string }> {
    return this.request<{ success: boolean; transaction_id: string }>(
      '/transactions',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async getPrintData(transactionId: string): Promise<PrintData | null> {
    try {
      return await this.request<PrintData>(`/fiscal/receipt/${transactionId}`)
    } catch (err) {
      if (err instanceof Error && err.message.startsWith('HTTP 404')) {
        return null
      }
      throw err
    }
  }

  async pollPrintData(transactionId: string): Promise<PrintData | null> {
    for (let attempt = 0; attempt < APP.PRINT_DATA_MAX_ATTEMPTS; attempt++) {
      const data = await this.getPrintData(transactionId)
      if (data) return data
      await this.delay(APP.PRINT_DATA_POLL_INTERVAL)
    }
    return null
  }

  async heartbeat(diagnostics: DiagnosticHeaders): Promise<boolean> {
    try {
      const stats = this.getStats()
      await this.request<{ status: string }>('/health', {
        headers: {
          'X-TPE-Status': diagnostics.tpeStatus,
          'X-Printer-Status': diagnostics.printerStatus,
          'X-Tickets-Printed': String(diagnostics.ticketsPrinted),
          'X-TPE-Paper-Status': diagnostics.tpePaperStatus,
          'X-Conn-Error-Rate': stats.errorRate,
          'X-Conn-Avg-Response': stats.avgResponse,
          'X-Conn-Total-Requests': String(stats.totalRequests),
        },
      })
      return true
    } catch {
      return false
    }
  }

  getStats(): { errorRate: string; avgResponse: string; totalRequests: number } {
    const { totalRequests, failedRequests, responseTimes } = this.stats
    const errorRate = totalRequests > 0
      ? ((failedRequests / totalRequests) * 100).toFixed(1)
      : '0.0'
    const avgResponse = responseTimes.length > 0
      ? (responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length).toFixed(2)
      : '0.00'
    return { errorRate, avgResponse, totalRequests }
  }

  async executeUnlockSequence(): Promise<boolean> {
    try {
      const result = await this.request<{ success: boolean }>(
        '/execute-unlock-sequence',
        { method: 'POST' },
      )
      return result.success
    } catch {
      return false
    }
  }

  // ---- utils --------------------------------------------------------------

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

// ---------------------------------------------------------------------------
// Diagnostic headers type
// ---------------------------------------------------------------------------

export interface DiagnosticHeaders {
  tpeStatus: string
  printerStatus: string
  ticketsPrinted: number
  tpePaperStatus: string
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const agentClient = new AgentClient()
